import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  PageBody,
  Spinner,
  Switch,
  SelectField,
  TextInput,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";

interface RewardScheme {
  id: string;
  name: string;
  description: string;
  status: "draft" | "active" | "paused" | "expired";
  conditions: Array<{
    type: "scan_count" | "product_scan_count" | "scan_streak";
    threshold: number;
    productId?: string;
  }>;
  reward: { bonusPoints: number; message?: string };
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
  maxRedemptions: number | null;
  redemptionCount: number;
  createdAt: string;
  updatedAt: string;
  _resolved?: {
    conditions: Array<{
      type: string;
      threshold: number;
      productId?: string;
      productName?: string;
    }>;
  };
}

interface Product {
  id: string;
  name: string;
  sku: string;
}

export function RewardSchemesPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [schemes, setSchemes] = useState<RewardScheme[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<RewardScheme> | null>(null);
  const [deleting, setDeleting] = useState<RewardScheme | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, prods] = await Promise.all([
        api.get<{ items: RewardScheme[] }>("/org/schemes"),
        api.get<{ items: Product[] }>("/org/products").catch(() => ({ items: [] })),
      ]);
      setSchemes(data.items);
      setProducts(prods.items);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load schemes.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = schemes.filter(
    (s) => !search || s.name.toLowerCase().includes(search.toLowerCase()),
  );

  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        name: editing.name ?? "",
        description: editing.description ?? "",
        status: editing.status ?? "active",
        conditions: editing.conditions ?? [],
        reward: editing.reward ?? { bonusPoints: 10 },
        priority: editing.priority ?? 0,
        startsAt: editing.startsAt,
        endsAt: editing.endsAt,
        maxRedemptions: editing.maxRedemptions,
      };
      if (editing.id) {
        await api.patch(`/org/schemes/${editing.id}`, payload);
        toast.success("Scheme updated.");
      } else {
        await api.post("/org/schemes", payload);
        toast.success("Scheme created.");
      }
      setEditing(null);
      void load();
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      } else {
        toast.error("Could not save scheme.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus(scheme: RewardScheme) {
    const nextStatus = scheme.status === "active" ? "paused" : "active";
    try {
      await api.patch(`/org/schemes/${scheme.id}`, { status: nextStatus });
      toast.success(`Scheme ${nextStatus === "active" ? "activated" : "paused"}.`);
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update.");
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await api.del(`/org/schemes/${deleting.id}`);
      toast.success("Scheme deleted.");
      setDeleting(null);
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete.");
    }
  }

  if (loading) {
    return (
      <PageBody>
        <div className="grid min-h-[40vh] place-items-center">
          <Spinner className="size-6 text-brand" />
        </div>
      </PageBody>
    );
  }

  return (
    <PageBody>
      <div className="flex w-full flex-col gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-title text-xl font-semibold">Reward schemes</h2>
            <p className="text-sm text-muted">
              Create dynamic rules that award bonus points based on scan activity.
            </p>
          </div>
          <Button onClick={() => setEditing({})}>
            <Plus className="mr-2 size-4" /> New scheme
          </Button>
        </div>

        {schemes.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
            <TextInput
              className="pl-9"
              label="Search"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
              value={search}
            />
          </div>
        )}

        {schemes.length === 0 ? (
          <Card>
            <CardBody>
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <p className="font-medium">No reward schemes yet</p>
                <p className="text-sm text-muted">
                  Create your first scheme to reward karigars for meeting scan milestones.
                </p>
                <Button className="mt-2" onClick={() => setEditing({})}>
                  Create a scheme
                </Button>
              </div>
            </CardBody>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filtered.map((s) => (
              <Card key={s.id}>
                <CardBody className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-title text-lg font-semibold">{s.name}</h3>
                        <StatusBadge status={s.status} />
                      </div>
                      {s.description && (
                        <p className="mt-1 text-sm text-muted">{s.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={s.status === "active"}
                        label={s.status === "active" ? "Active" : "Inactive"}
                        onChange={(next) => {
                          void (async () => {
                            try {
                              await api.patch(`/org/schemes/${s.id}`, { status: next ? "active" : "paused" });
                              toast.success(`Scheme ${next ? "activated" : "paused"}.`);
                              void load();
                            } catch {
                              // reloads current state
                            }
                          })();
                        }}
                      />
                      <Button variant="secondary" size="sm" onClick={() => setEditing(s)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => setDeleting(s)}>
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                  <SchemeSummary scheme={s} />
                </CardBody>
              </Card>
            ))}
          </div>
        )}

        {editing && (
          <SchemeEditor
            draft={editing}
            products={products}
            saving={saving}
            onCancel={() => setEditing(null)}
            onChange={setEditing}
            onSave={() => void save()}
          />
        )}

        <ConfirmDialog
          confirmLabel="Delete"
          message={`Delete "${deleting?.name}"? Karigars can no longer earn this bonus.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => void confirmDelete()}
          open={!!deleting}
          title="Delete scheme?"
        />
      </div>
    </PageBody>
  );
}

function StatusBadge({ status }: { status: RewardScheme["status"] }) {
  const map: Record<RewardScheme["status"], { tone: "success" | "neutral" | "warning"; label: string }> = {
    active: { tone: "success", label: "Active" },
    paused: { tone: "warning", label: "Paused" },
    draft: { tone: "neutral", label: "Draft" },
    expired: { tone: "neutral", label: "Expired" },
  };
  const m = map[status];
  return <Badge tone={m.tone}>{m.label}</Badge>;
}

function SchemeSummary({ scheme }: { scheme: RewardScheme }) {
  const conditions = scheme._resolved?.conditions ?? scheme.conditions;
  return (
    <div className="rounded-lg border border-rule bg-surface p-3 text-sm">
      <p className="text-muted">
        <span className="font-medium text-ink">When:</span>{" "}
        {conditions
          .map((c) => {
            if (c.type === "product_scan_count" && "productName" in c && c.productName) {
              return `${c.threshold} scans of ${c.productName}`;
            }
            if (c.type === "scan_count") {
              return `${c.threshold} total scans`;
            }
            if (c.type === "scan_streak") {
              return `${c.threshold} scans in last ${c.threshold} days`;
            }
            return "";
          })
          .join(" AND ")}
      </p>
      <p className="mt-1 text-muted">
        <span className="font-medium text-ink">Then:</span> Award{" "}
        <span className="font-semibold text-success">+{scheme.reward.bonusPoints} points</span>
        {scheme.reward.message ? ` (${scheme.reward.message})` : ""}
      </p>
      <p className="mt-1 text-xs text-muted">
        {scheme.redemptionCount} redemption{scheme.redemptionCount === 1 ? "" : "s"} so far
        {scheme.maxRedemptions ? ` (max ${scheme.maxRedemptions})` : ""}
      </p>
    </div>
  );
}

interface SchemeEditorProps {
  draft: Partial<RewardScheme>;
  products: Product[];
  saving: boolean;
  onChange: (next: Partial<RewardScheme>) => void;
  onSave: () => void;
  onCancel: () => void;
}

function SchemeEditor({ draft, products, saving, onChange, onSave, onCancel }: SchemeEditorProps) {
  const conditions = draft.conditions ?? [
    { type: "scan_count" as const, threshold: 10 },
  ];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardBody className="flex flex-col gap-4">
          <h2 className="font-title text-xl font-semibold">
            {draft.id ? "Edit scheme" : "New reward scheme"}
          </h2>

          <TextInput
            label="Name"
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder="e.g. 10+ scans bonus"
            value={draft.name ?? ""}
          />

          <TextInput
            label="Description"
            onChange={(e) => onChange({ ...draft, description: e.target.value })}
            placeholder="What this scheme rewards"
            value={draft.description ?? ""}
          />

          <div className="rounded-lg border border-rule p-4">
            <p className="mb-3 text-sm font-medium">Conditions</p>
            {conditions.map((c, idx) => (
              <div key={idx} className="mb-3 rounded-lg border border-rule bg-surface p-3 last:mb-0">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-medium">Condition {idx + 1}</p>
                  {conditions.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onChange({
                        ...draft,
                        conditions: conditions.filter((_, conditionIndex) => conditionIndex !== idx),
                      })}
                    >
                      <Trash2 className="size-4" /> Remove
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <SelectField
                    label="Condition type"
                    onChange={(e) => {
                      const next = [...conditions];
                      next[idx] = { ...c, type: e.target.value as "scan_count" | "product_scan_count" | "scan_streak" };
                      onChange({ ...draft, conditions: next });
                    }}
                    options={[
                      { value: "scan_count", label: "Total QR scans" },
                      { value: "product_scan_count", label: "Scans of a specific product" },
                      { value: "scan_streak", label: "Scans in last N days" },
                    ]}
                    value={c.type}
                  />
                  <TextInput
                    label="Required scans"
                    min={1}
                    onChange={(e) => {
                      const next = [...conditions];
                      next[idx] = { ...c, threshold: Number(e.target.value) };
                      onChange({ ...draft, conditions: next });
                    }}
                    type="number"
                    value={String(c.threshold)}
                  />
                  {c.type === "product_scan_count" && (
                    <SelectField
                      className="sm:col-span-2"
                      label="Product"
                      onChange={(e) => {
                        const next = [...conditions];
                        next[idx] = { ...c, productId: e.target.value };
                        onChange({ ...draft, conditions: next });
                      }}
                      options={[
                        { value: "", label: "Select a product..." },
                        ...products.map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` })),
                      ]}
                      value={c.productId ?? ""}
                    />
                  )}
                </div>
              </div>
            ))}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                onChange({
                  ...draft,
                  conditions: [...conditions, { type: "scan_count", threshold: 10 }],
                });
              }}
            >
              + Add condition
            </Button>
            <p className="mt-2 text-xs text-muted">
              Multiple conditions are AND-ed. All must be true to award.
            </p>
          </div>

          <div className="rounded-lg border border-rule p-4">
            <p className="mb-3 text-sm font-medium">Reward</p>
            <div className="grid grid-cols-2 gap-2">
              <TextInput
                label="Bonus points"
                min={1}
                onChange={(e) =>
                  onChange({
                    ...draft,
                    reward: { ...(draft.reward ?? { bonusPoints: 10 }), bonusPoints: Number(e.target.value) },
                  })
                }
                type="number"
                value={String(draft.reward?.bonusPoints ?? 10)}
              />
              <SelectField
                label="Status"
                onChange={(e) => onChange({ ...draft, status: e.target.value as RewardScheme["status"] })}
                options={[
                  { value: "active", label: "Active" },
                  { value: "paused", label: "Paused" },
                  { value: "draft", label: "Draft" },
                ]}
                value={draft.status ?? "active"}
              />
              <TextInput
                className="col-span-2"
                label="Message (optional)"
                onChange={(e) =>
                  onChange({
                    ...draft,
                    reward: { ...(draft.reward ?? { bonusPoints: 10 }), message: e.target.value },
                  })
                }
                placeholder="Shown to the karigar when they earn this"
                value={draft.reward?.message ?? ""}
              />
            </div>
          </div>

          <TextInput
            label="Max redemptions (optional)"
            min={1}
            onChange={(e) =>
              onChange({
                ...draft,
                maxRedemptions: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            placeholder="Leave empty for unlimited"
            type="number"
            value={draft.maxRedemptions ? String(draft.maxRedemptions) : ""}
          />

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSave}>
              {draft.id ? "Save changes" : "Create scheme"}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
